import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { DragulaService } from 'ng2-dragula';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import * as FlatServices from '@medsurf/flat-services';
import * as FlatDesigns from '@medsurf/flat-designs';
import { ModalControlComponent, DragContainerComponent } from '@medsurf/flat-components';
import { MediaLibraryComponent } from '../media-library/media-library.component';

@Component({
    selector: 'medsurf-common-slide-tree',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './slide-tree.component.html',
    styleUrls: ['./slide-tree.component.scss'],
    standalone: false
})
export class SlideTreeComponent extends DragContainerComponent {
	@Output() openEditOverlay = new EventEmitter();
	
	@ViewChild('template_slide_tree', { static: false }) templateSlideTree: FlatDesigns.TemplateSlideTreeContentDesign;
	
  getSlideLayersMenuItems = () => {
    const copyLayerSet = this.bulkEditAuthorFacade.snapshot_copyLayerSet();
    return [
      {
        label: this.translate.instant('global.layer_add'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.LAYER,
        command: () => this.#addLayer(),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.layer_paste'),
        disabled: copyLayerSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteLayer(),
      },
    ] satisfies MenuItem[];
  }
  getSlideGroupsMenuItems = () => {
    const copyGroupSet = this.bulkEditAuthorFacade.snapshot_copyGroupSet();
    return [
      {
        label: this.translate.instant('global.group_add'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.GROUP,
        command: () => this.#addGroup(),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.group_paste'),
        disabled: copyGroupSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteGroup(),
      },
    ] satisfies MenuItem[];
  }
  getSlideQuestionsMenuItems = () => {
    const copyQuestionSet = this.bulkEditAuthorFacade.snapshot_copyQuestionSet();
    return [
      {
        label: this.translate.instant('global.question_add'),
        disabled: true,
        styleClass: 'menu_grouping_label',
      },
      {
        label: this.translate.instant('enum.question.LongListQuestion'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_LONG_LIST,
        command: () => this.#addQuestion(FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion),
      },
      {
        label: this.translate.instant('enum.question.TrueFalseQuestion'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_TRUE_FALSE,
        command: () =>
          this.#addQuestion(FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion),
      },
      {
        label: this.translate.instant('enum.question.FreeTextQuestion'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_FREE_TEXT,
        command: () =>
          this.#addQuestion(FlatModels.QuestionEntityModels.QuestionType.enum.FreeTextQuestion),
      },
      {
        label: this.translate.instant('enum.question.IndicationQuestion'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_INDICATION,
        command: () =>
          this.#addQuestion(FlatModels.QuestionEntityModels.QuestionType.enum.IndicationQuestion),
      },
      {
        label: this.translate.instant('enum.question.MappingQuestion'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_MAPPING,
        command: () => this.#addQuestion(FlatModels.QuestionEntityModels.QuestionType.enum.MappingQuestion),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.question_paste'),
        disabled: copyQuestionSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteQuestion(),
      },
    ] satisfies MenuItem[];
  }
  getLayerMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "layer") return;
    const {layer} = data;
    const copyLayerSet = this.bulkEditAuthorFacade.snapshot_copyLayerSet();
    return [
      {
        label: this.translate.instant('global.layer_copy'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPY,
        command: () => this.#copyLayer(layer),
      },
      {
        label: this.translate.instant('global.layer_paste'),
        disabled: copyLayerSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteLayer(),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.layer_remove'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
        styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
        command: () => this.#removeLayer(layer),
      },
    ] satisfies MenuItem[];
  }
  getLayerImagesMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== 'layerImages') return;
    const {layer} = data;
    const copyImageSet = this.bulkEditAuthorFacade.snapshot_copyImageSet();
    return [
      {
        label: this.translate.instant('global.image_add'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.MEDIA_VISUAL,
        command: () => this.#addImage(layer),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.image_paste'),
        disabled: copyImageSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteImage(layer),
      }
    ] satisfies MenuItem[];
  }
  getImageMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "image") return;
    const {layer, image} = data;
    const copyImageSet = this.bulkEditAuthorFacade.snapshot_copyImageSet();
    return [
      {
        label: this.translate.instant('global.image_exchange'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.SEARCH_MEDIA,
        command: () => this.#exchangeImage(image),
      },
      {
        label: this.translate.instant('global.image_delete'),
        disabled: !image.media,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.TIMES,
        command: () => this.#deleteImage(image),
      },
      {
        label: this.translate.instant('global.image_set_start_image'),
        visible: layer.images.length > 1,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.STAR,
        command: () => this.#setStartImage(image),
      },
      {
        separator: true
      },
      {
        label: this.translate.instant('global.image_copy'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPY,
        command: () => this.#copyImage(image),
      },
      {
        label: this.translate.instant('global.image_paste'),
        disabled: copyImageSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteImage(layer),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.image_remove'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
        styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
        command: () => this.#removeImage(image),
      }
    ] satisfies MenuItem[];
  }
  getImageAnnotationsMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "imageAnnotations") return;
    const {image} = data;
    const copyAnnotationSet = this.bulkEditAuthorFacade.snapshot_copyAnnotationSet();
    const disabled = copyAnnotationSet.size === 0;
    return [
      {
        label: this.translate.instant('global.annotation_add'),
        disabled: true,
        styleClass: 'menu_grouping_label',
      },
      {
        label: this.translate.instant('global.marker'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_MARKER,
        command: () => this.#addMarker(image, FlatModels.AnnotationEntityModels.TipType.enum.dot),
      },
      {
        label: this.translate.instant('global.wedge'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_WEDGE,
        command: () => this.#addFreeForm(image, FlatModels.AnnotationEntityModels.Shape.enum.triangle),
      },
      {
        label: this.translate.instant('global.arrow'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_ARROW,
        command: () => this.#addFreeForm(image, FlatModels.AnnotationEntityModels.Shape.enum.arrow),
      },
      {
        label: this.translate.instant('global.line'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_LINE,
        command: () => this.#addFreeForm(image, FlatModels.AnnotationEntityModels.Shape.enum.line),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.rectangle'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_RECTANGLE,
        command: () => this.#addFreeForm(image, FlatModels.AnnotationEntityModels.Shape.enum.rectangle),
      },
      {
        label: this.translate.instant('global.ellipse'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_ELLIPSE,
        command: () => this.#addFreeForm(image, FlatModels.AnnotationEntityModels.Shape.enum.ellipse),
      },
      {
        label: this.translate.instant('global.free_form'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_FREE,
        command: () => this.#addFreeForm(image, FlatModels.AnnotationEntityModels.Shape.enum.free),
      },
      {
        label: this.translate.instant('global.interactive_area'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_INTERACTIVEAREA,
        command: () => this.#addFreeForm(image, FlatModels.AnnotationEntityModels.Shape.enum.interactiveArea),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.text'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_TEXT,
        command: () => this.#addText(image),
      },
      {
        label: this.translate.instant('global.legend'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_KEYMAP,
        command: () => this.#addKeymap(image),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant((copyAnnotationSet.size === 1 ? 'global.annotations_paste_singular' : 'global.annotations_paste_plural'), {count: copyAnnotationSet.size}),
        disabled: true,
        styleClass: disabled ? '' : 'menu_grouping_label',
      },
      {
        label: this.translate.instant('global.annotation_paste'),
        disabled: disabled,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteAnnotation(image),
      },
      {
        label: this.translate.instant('global.annotation_paste_position'),
        disabled: disabled,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteAnnotationWithPosition(image),
      },
    ] satisfies MenuItem[];
  }
  getImagePoisMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "imagePois") return;
    const {image} = data;
    const copyPoiSet = this.bulkEditAuthorFacade.snapshot_copyPoiSet();
    return [
      {
        label: this.translate.instant('global.poi_add'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.SIGN_POST,
        command: () => this.#addPoi(image),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.poi_paste'),
        disabled: copyPoiSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pastePoi(image),
      },
    ] satisfies MenuItem[];
  }
  getAnnotationMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "annotation") return;
    const {image, images, annotation, group, poi} = data;
    const copyAnnotationSet = this.bulkEditAuthorFacade.snapshot_copyAnnotationSet();
    const disabled = copyAnnotationSet.size === 0;
    return [
      {
        label: this.translate.instant('global.annotation_go_to'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.LOCATE,
        command: () => this.goToAnnotation(annotation),
      },
      {
        label: this.translate.instant('global.annotation_update_position'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.RELOAD,
        command: () => this.#updateAnnotationPosition(annotation),
      },
      {
        label: this.translate.instant('global.annotation_image_remove'),
        visible: image !== undefined,
        disabled: (images ? images.length : 0) === 1,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.UNLINK,
        command: () => image && this.#removeAnnotationFromImage(image, annotation),
      },
      {
        label: this.translate.instant('global.annotation_group_remove'),
        visible: group !== undefined,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.UNLINK,
        command: () => group && this.#removeAnnotationFromGroup(group, annotation),
      },
      {
        label: this.translate.instant('global.annotation_poi_remove'),
        visible: poi !== undefined,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.UNLINK,
        command: () => poi && this.#removeAnnotationFromPoi(poi, annotation),
      },
      {
        separator: true
      },
      {
        label: this.translate.instant('global.annotation_copy'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPY,
        command: () => this.#copyAnnotation(annotation),
      },
      {
        separator: true
      },
      {
        label: this.translate.instant((copyAnnotationSet.size === 1 ? 'global.annotations_paste_singular' : 'global.annotations_paste_plural'), {count: copyAnnotationSet.size}),
        disabled: true,
        styleClass: disabled ? '' : 'menu_grouping_label',
      },
      {
        label: this.translate.instant('global.annotation_paste'),
        visible: image !== undefined,
        disabled: disabled,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => image && this.#pasteAnnotation(image),
      },
      {
        label: this.translate.instant('global.annotation_paste_position'),
        visible: image !== undefined,
        disabled: disabled,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => image && this.#pasteAnnotationWithPosition(image),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.annotation_remove'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
        styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
        command: () => this.#removeAnnotation(annotation),
      }
    ] satisfies MenuItem[];
  }
  getGroupMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "group") return;
    const {group} = data;
    const copyGroupSet = this.bulkEditAuthorFacade.snapshot_copyGroupSet();
    return [
      {
        label: this.translate.instant('global.group_copy'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPY,
        command: () => this.#copyGroup(group),
      },
      {
        label: this.translate.instant('global.group_paste'),
        disabled: copyGroupSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteGroup(),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.group_remove'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
        styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
        command: () => this.#removeGroup(group),
      },
    ] satisfies MenuItem[];
  }
  getImagePoiMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "imagePoi") return;
    const {poi, image, images} = data;
    const copyPoiSet = this.bulkEditAuthorFacade.snapshot_copyPoiSet();
    return [
      {
        label: this.translate.instant('global.poi_go_to'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.LOCATE,
        command: () => this.goToPoi(poi),
      },
      {
        label: this.translate.instant('global.poi_update_position'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.RELOAD,
        command: () => this.#updatePoiPosition(poi),
      },
      {
        label: this.translate.instant('global.poi_image_remove'),
        visible: poi !== undefined,
        disabled: images.length === 1,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.UNLINK,
        command: () => poi && this.#removePoiFromImage(image, poi),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.poi_copy'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPY,
        command: () => this.#copyPoi(poi),
      },
      {
        label: this.translate.instant('global.poi_paste'),
        disabled: copyPoiSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pastePoi(image),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.poi_remove'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
        styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
        command: () => this.#removePoi(poi),
      },
    ] satisfies MenuItem[];
  }
  getQuestionMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "question") return;
    const {question} = data;
    const copyQuestionSet = this.bulkEditAuthorFacade.snapshot_copyQuestionSet();
	  return [
		  {
			  label: this.translate.instant('global.question_copy'),
			  icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPY,
			  command: () => this.#copyQuestion(question),
		  },
		  {
			  label: this.translate.instant('global.question_paste'),
			  disabled: copyQuestionSet.size === 0,
			  icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
			  command: () => this.#pasteQuestion(),
		  },
		  {
			  separator: true,
		  },
		  {
			  label: this.translate.instant('global.question_remove'),
			  icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
			  styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
			  command: () => this.#removeQuestion(question),
		  },
	  ] satisfies MenuItem[];
  }
  readonly FlatModels = FlatModels;

  constructor(public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
              private alertControlFacade: FlatFacades.AlertControlFacade,
              public mediaControlService: FlatServices.MediaControlService,
              private dialogService: DialogService,
              public translate: TranslateService,
              dragulaService: DragulaService) {
    super(dragulaService);
  }

  goHome() {
    this.slideAuthorFacade.requestGoHome().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }
  
  onChangeEditPanelDisplay(editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay) {
    this.slideAuthorFacade.requestSetEditPanelDisplay(editPanelDisplay).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.openEditOverlay.emit()
    });
  }

  //<editor-fold desc="Drag And Drop">

  onLayerMoved(layers: FlatModels.LayerEntityModels.Layer[]) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;

    this.slideAuthorFacade.requestRearrangeLayers(pageClone, layers).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  onImageMoved(layer: FlatModels.LayerEntityModels.Layer,
               images: FlatModels.ImageEntityModels.Image[]) {
    const layerClone = structuredClone(layer);

    this.slideAuthorFacade.requestRearrangeImages(layerClone, images).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  onQuestionMoved(questions: FlatModels.QuestionEntityModels.QuestionEntityType[]) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;

    this.slideAuthorFacade.requestRearrangeQuestions(pageClone, questions).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  onImagePoiMoved(image: FlatModels.ImageEntityModels.Image,
                  pois: FlatModels.PointOfInterestEntityModels.PointOfInterest[]) {
    const imageClone = structuredClone(image);

    this.slideAuthorFacade.requestRearrangeImagePois(imageClone, pois).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  //</editor-fold>

  //<editor-fold desc="Layer Edit">

  setLayerIndex(event: MouseEvent,
                layerIndex: number,
                layerId: string) {
    if (this.templateSlideTree.handled) {
      this.templateSlideTree.handled = false;
      return;
    }
    document.getSelection()?.removeAllRanges();
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestToggleLayer(layerId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    } else {
      this.slideAuthorFacade.requestSetLayerIndex(layerIndex).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    }
  }

  #addLayer() {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;

    this.slideAuthorFacade.requestAddLayer(pageClone, 'history.layer_add').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onSlideLayersExpand(pageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  #removeLayer(layer: FlatModels.LayerEntityModels.Layer) {
    const layerClone = structuredClone(layer);
    
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.layer_remove'),
      data: {
        label: 'global.layer_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        this.slideAuthorFacade.requestRemoveLayer(layerClone, 'history.layer_remove').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  #copyLayer(layer: FlatModels.LayerEntityModels.Layer) {
    this.bulkEditAuthorFacade.requestCopyLayer(layer.id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #pasteLayer() {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;

    this.bulkEditAuthorFacade.requestPasteLayers(pageClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onSlideLayersExpand(pageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  //</editor-fold>

  //<editor-fold desc="Image Edit">

  setImageIndex(event: MouseEvent,
                layerIndex: number,
                imageIndex: number,
                imageId: string) {
    if (this.templateSlideTree.handled) {
      this.templateSlideTree.handled = false;
      return;
    }
    document.getSelection()?.removeAllRanges();
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestToggleImage(imageId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    } else {
      return this.slideAuthorFacade.requestSetImageIndex(layerIndex, imageIndex).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    }
  }

  #addImage(layer: FlatModels.LayerEntityModels.Layer) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const layerClone = structuredClone(layer);
    
    const dialogRef = this.dialogService.open(MediaLibraryComponent, {
      header: this.translate.instant('global.image_choose'),
      data: {
        isModal: true,
        multiple: false,
        allowedTypes: [
          FlatModels.MediaEntityModels.MediaType.enum.MediaImage,
          FlatModels.MediaEntityModels.MediaType.enum.MediaDeepzoom,
          FlatModels.MediaEntityModels.MediaType.enum.MediaVideo,
          FlatModels.MediaEntityModels.MediaType.enum.MediaAudio
        ]
      } satisfies FlatModels.ModalAuthorModels.MediaLibraryModalData,
      dismissableMask: true,
      width: '90%',
      height: '90%',
      maximizable: true,
      rtl: true
    });
    dialogRef.onClose.subscribe((media) => {
      if (media && pageClone) {
        this.slideAuthorFacade.requestAddImageAndThumbnail(pageClone, layerClone, media.id).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            this.templateSlideTree.onLayerExpand(layerClone, true);
            const image = this.slideAuthorFacade.snapshot_image();
            if (image) this.templateSlideTree.onImageExpand(image, true);
            this.templateSlideTree.handled = false;
          }
        });
      }
    });
  }

  #removeImage(image: FlatModels.ImageEntityModels.Image) {
    const imageClone = structuredClone(image);
    
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.image_remove'),
      data: {
        label: 'global.image_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        this.slideAuthorFacade.requestRemoveImage(imageClone, 'history.image_remove').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  #removeAnnotationFromImage(image: FlatModels.ImageEntityModels.Image,
                             annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    const imageClone = structuredClone(image);

    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.annotation_image_remove'),
      data: {
        label: 'global.annotation_image_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        // Request Remove Annotation From Image
        this.slideAuthorFacade.requestRemoveAnnotationFromImage(imageClone, annotation.id, 'history.annotation_remove_from_image').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  #removePoiFromImage(image: FlatModels.ImageEntityModels.Image,
                      poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    const imageClone = structuredClone(image);

    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.image_poi_remove'),
      data: {
        label: 'global.image_poi_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        // Request Remove Poi From Image
        this.slideAuthorFacade.requestRemovePoiFromImage(imageClone, poi.id, 'history.poi_remove_from_image').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  #exchangeImage(image: FlatModels.ImageEntityModels.Image) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const imageClone = structuredClone(image);

    const dialogRef = this.dialogService.open(MediaLibraryComponent, {
      header: this.translate.instant('global.image_choose'),
      data: {
        isModal: true,
        multiple: false,
        selected: image.media ? { id: image.media } : undefined,
        allowedTypes: [
          FlatModels.MediaEntityModels.MediaType.enum.MediaImage,
          FlatModels.MediaEntityModels.MediaType.enum.MediaDeepzoom,
          FlatModels.MediaEntityModels.MediaType.enum.MediaVideo,
          FlatModels.MediaEntityModels.MediaType.enum.MediaAudio
        ]
      },
      dismissableMask: true,
      width: '90%',
      height: '90%',
      maximizable: true,
      rtl: true
    });
    dialogRef.onClose.subscribe((media) => {
      if (media) {
        this.slideAuthorFacade.requestUpdateImageAndThumbnail(pageClone, imageClone, media.id, 'history.image_update').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  #deleteImage(image: FlatModels.ImageEntityModels.Image) {
    // Update Image
    const imageClone = structuredClone(image);
    imageClone.media = null;

    this.slideAuthorFacade.requestUpdateImage(imageClone, 'history.image_update').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #setStartImage(image: FlatModels.ImageEntityModels.Image) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const imageClone = structuredClone(image);

    this.slideAuthorFacade.requestSetStartImage(pageClone, imageClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #copyImage(image: FlatModels.ImageEntityModels.Image) {
    this.bulkEditAuthorFacade.requestCopyImage(image.id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #pasteImage(layer: FlatModels.LayerEntityModels.Layer) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const layerClone = structuredClone(layer);

    this.bulkEditAuthorFacade.requestPasteImages(pageClone, layerClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onLayerExpand(layerClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  //</editor-fold>

  //<editor-fold desc="Annotation Edit">

  setAnnotationId(event: MouseEvent,
                  annotationId: string) {
    if (this.templateSlideTree.handled) {
      this.templateSlideTree.handled = false;
      return;
    }
    document.getSelection()?.removeAllRanges();
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestToggleAnnotation(annotationId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    } else {
      return this.slideAuthorFacade.requestSetAnnotationId(annotationId, FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    }
  }

  #addMarker(image: FlatModels.ImageEntityModels.Image,
             tipType: FlatModels.AnnotationEntityModels.TipType = FlatModels.AnnotationEntityModels.TipType.enum.none) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const imageClone = structuredClone(image);

    this.slideAuthorFacade.requestAddMarkerWithPosition(pageClone, imageClone, null, tipType, null, 'history.marker_add').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onImageAnnotationsExpand(imageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  #addFreeForm(image: FlatModels.ImageEntityModels.Image,
               shape: FlatModels.AnnotationEntityModels.Shape = FlatModels.AnnotationEntityModels.Shape.enum.free) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const imageClone = structuredClone(image);

    this.slideAuthorFacade.requestAddFreeFormWithPosition(pageClone, imageClone, null, shape, null, 'history.free_form_add').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onImageAnnotationsExpand(imageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  #addKeymap(image: FlatModels.ImageEntityModels.Image) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const imageClone = structuredClone(image);

    this.slideAuthorFacade.requestAddKeymapWithPosition(pageClone, imageClone, null, null, 'history.keymap_add').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onImageAnnotationsExpand(imageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  #addText(image: FlatModels.ImageEntityModels.Image) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const imageClone = structuredClone(image);

    this.slideAuthorFacade.requestAddTextWithPosition(pageClone, imageClone, null, null, 'history.text_add').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onImageAnnotationsExpand(imageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  #removeAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    const annotationClone = structuredClone(annotation);

    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.annotation_remove'),
      data: {
        label: 'global.annotation_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        this.slideAuthorFacade.requestRemoveAnnotation(annotationClone, 'history.annotation_remove').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  goToAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    this.slideAuthorFacade.requestGoToAnnotation(annotation).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #updateAnnotationPosition(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    const annotationClone = structuredClone(annotation);

    this.slideAuthorFacade.requestUpdateAnnotationPosition(annotationClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #copyAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    this.bulkEditAuthorFacade.requestCopyAnnotation(annotation.id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #pasteAnnotation(image: FlatModels.ImageEntityModels.Image) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const imageClone = structuredClone(image);

    this.bulkEditAuthorFacade.requestPasteAnnotations(pageClone, imageClone, null).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onImageAnnotationsExpand(imageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  #pasteAnnotationWithPosition(image: FlatModels.ImageEntityModels.Image) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page())
    if (!pageClone) return;
    const imageClone = structuredClone(image);

    this.slideAuthorFacade.requestPasteAnnotationWithPosition(pageClone, imageClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onImageAnnotationsExpand(imageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  //</editor-fold>
  
  //<editor-fold desc="Group Edit">

  setGroupId(event: MouseEvent,
             groupId: string) {
    if (this.templateSlideTree.handled) {
      this.templateSlideTree.handled = false;
      return;
    }
    document.getSelection()?.removeAllRanges();
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestToggleGroup(groupId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    } else {
      this.slideAuthorFacade.requestSetGroupId(groupId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    }
  }

  #addGroup() {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;

    this.slideAuthorFacade.requestAddGroup(pageClone, null, 'history.annotation_group_add').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onSlideGroupsExpand(pageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  #removeGroup(group: FlatModels.GroupEntityModels.Group) {
    const groupClone = structuredClone(group);

    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.group_remove'),
      data: {
        label: 'global.group_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        this.slideAuthorFacade.requestRemoveGroup(groupClone, 'history.annotation_group_remove').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  #removeAnnotationFromGroup(group: FlatModels.GroupEntityModels.Group,
                             annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    const groupClone = structuredClone(group);

    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.annotation_group_remove'),
      data: {
        label: 'global.annotation_group_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        this.slideAuthorFacade.requestRemoveAnnotationFromGroup(groupClone, annotation.id, 'history.annotation_remove_from_group').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  #copyGroup(group: FlatModels.GroupEntityModels.Group) {
    this.bulkEditAuthorFacade.requestCopyGroup(group.id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #pasteGroup() {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;

    this.bulkEditAuthorFacade.requestPasteGroups(pageClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onSlideGroupsExpand(pageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  //</editor-fold>
  
  //<editor-fold desc="Poi Edit">

  setPoiId(event: MouseEvent,
           poiId: string) {
    if (this.templateSlideTree.handled) {
      this.templateSlideTree.handled = false;
      return;
    }
    document.getSelection()?.removeAllRanges();
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestTogglePoi(poiId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    } else {
      return this.slideAuthorFacade.requestSetPoiId(poiId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    }
  }

  #addPoi(image: FlatModels.ImageEntityModels.Image) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const imageClone = structuredClone(image);

    this.slideAuthorFacade.requestAddPoiWithPosition(pageClone, imageClone, null, 'history.poi_add').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onImagePoisExpand(imageClone, true);
        const poi = this.slideAuthorFacade.snapshot_poi();
        if (poi) {
          this.templateSlideTree.onPoiExpand(poi, true);
        }
        this.templateSlideTree.handled = false;
      }
    });
  }

  #removePoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    const poiClone = structuredClone(poi);

    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.poi_remove'),
      data: {
        label: 'global.poi_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        // Request Remove Poi
        this.slideAuthorFacade.requestRemovePoi(poiClone, 'history.poi_remove').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  #removeAnnotationFromPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
                           annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    const poiClone = structuredClone(poi);

    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.annotation_poi_remove'),
      data: {
        label: 'global.annotation_poi_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        this.slideAuthorFacade.requestRemoveAnnotationFromPoi(poiClone, annotation.id, 'history.annotation_remove_from_poi').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  goToPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    this.slideAuthorFacade.requestGoToPoi(poi).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #updatePoiPosition(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    const poiClone = structuredClone(poi);

    this.slideAuthorFacade.requestUpdatePoiPosition(poiClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #copyPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    this.bulkEditAuthorFacade.requestCopyPoi(poi.id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #pastePoi(image: FlatModels.ImageEntityModels.Image) {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    const imageClone = structuredClone(image);
    this.bulkEditAuthorFacade.requestPastePois(pageClone, imageClone ? [imageClone] : null, true).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onImagePoisExpand(imageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  //</editor-fold>

  //<editor-fold desc="Question Edit">

  setQuestionId(event: MouseEvent,
                questionId: string) {
    if (this.templateSlideTree.handled) {
      this.templateSlideTree.handled = false;
      return;
    }
    document.getSelection()?.removeAllRanges();
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestToggleQuestion(questionId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    } else {
      // Request Set Question Id
      return this.slideAuthorFacade.requestSetQuestionId(questionId, FlatModels.SlideAuthorModels.EditPanelDisplay.enum.metadata).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.openEditOverlay.emit()
      });
    }
  }

  #addQuestion(questionType: FlatModels.QuestionEntityModels.QuestionType = 'TrueFalseQuestion') {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    this.slideAuthorFacade.requestAddQuestion(pageClone, questionType).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onSlideQuestionsExpand(pageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  #removeQuestion(question: FlatModels.QuestionEntityModels.QuestionEntityType) {
    const questionClone = structuredClone(question);
    if (!questionClone) {
      return;
    }
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.question_remove'),
      data: {
        label: 'global.question_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        this.slideAuthorFacade.requestRemoveQuestion(questionClone, 'history.question_remove').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.templateSlideTree.handled = false,
        });
      }
    });
  }

  #copyQuestion(question: FlatModels.QuestionEntityModels.QuestionEntityType) {
    this.bulkEditAuthorFacade.requestCopyQuestion(question.id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateSlideTree.handled = false,
    });
  }

  #pasteQuestion() {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page());
    if (!pageClone) return;
    this.bulkEditAuthorFacade.requestPasteQuestions(pageClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.templateSlideTree.onSlideQuestionsExpand(pageClone, true);
        this.templateSlideTree.handled = false;
      }
    });
  }

  //</editor-fold>
}
